import { axiosInstance, ENDPOINT } from 'common/constants';
import { handleResponse, humanize } from 'common/helpers';
import { isPartnerAdmin, isPetasenseAdmin, isPetasenseViewer } from 'common/rbac/util';
import { convertTimestringsToDateObjects, round, formatDateWithLocalizedMonthAbbr } from 'common/utils';
import { getCurrencySymbol } from 'common/utils/currencyUtils';
import { widgetConstants, keyMetadataMap } from '../constants/widgets.constants';
import i18n from '../../../i18n/config';

const LABEL_TO_I18N_KEY = {
  reachable: 'widgetLibrary.chartLabels.reachable',
  unreachable: 'widgetLibrary.chartLabels.unreachable',
  unassigned: 'widgetLibrary.chartLabels.unassigned',
  '> 20%': 'widgetLibrary.chartLabels.above20',
  '< 20%': 'widgetLibrary.chartLabels.lessThan20',
  '< 10%': 'widgetLibrary.chartLabels.lessThan10',
  unknown: 'widgetLibrary.chartLabels.unknown',
  strong: 'widgetLibrary.chartLabels.strong',
  good: 'widgetLibrary.chartLabels.good',
  normal: 'widgetLibrary.chartLabels.normal',
  weak: 'widgetLibrary.chartLabels.weak',
  'very weak': 'widgetLibrary.chartLabels.veryWeak',
  vfd_rotating: 'widgetLibrary.chartLabels.vfdRotating',
  non_vfd_rotating: 'widgetLibrary.chartLabels.nonVfdRotating',
  other: 'widgetLibrary.chartLabels.other',
  pole_count_speed: 'widgetLibrary.chartLabels.poleCountSpeed',
  ON: 'widgetLibrary.chartLabels.on',
  OFF: 'widgetLibrary.chartLabels.off',
  NA: 'widgetLibrary.chartLabels.na',
  open: 'widgetLibrary.chartLabels.open',
  closed: 'widgetLibrary.chartLabels.closed',
  active: 'widgetLibrary.chartLabels.active',
  inactive: 'widgetLibrary.chartLabels.inactive',
  completed: 'widgetLibrary.chartLabels.completed',
  review_pending: 'widgetLibrary.chartLabels.reviewPending',
  total_count: 'widgetLibrary.chartLabels.totalCount',
  '< 10': 'widgetLibrary.chartLabels.lessThan10',
  '< 8': 'widgetLibrary.chartLabels.lessThan8',
  '< 7': 'widgetLibrary.chartLabels.lessThan7',
  '< 6': 'widgetLibrary.chartLabels.lessThan6',
  '< 4': 'widgetLibrary.chartLabels.lessThan4',
  '< 2': 'widgetLibrary.chartLabels.lessThan2',
  machines_with_alarm_but_no_task: 'widgetLibrary.chartLabels.machinesWithAlarmButNoTask',
  machines_with_task_but_no_alarm: 'widgetLibrary.chartLabels.machinesWithTaskButNoAlarm',
  machines_with_alarm_task_but_no_updates: 'widgetLibrary.chartLabels.machinesWithAlarmTaskButNoUpdates',
  machines_with_recent_open_alarms: 'widgetLibrary.chartLabels.machinesWithRecentOpenAlarms',
  recent_open_alarms: 'widgetLibrary.chartLabels.recentOpenAlarms',
  device_unreachable: 'widgetLibrary.chartLabels.deviceUnreachable',
  unread_events: 'widgetLibrary.widgets.unread_events',
  analysed: 'widgetLibrary.chartLabels.analysed',
  not_analysed: 'widgetLibrary.chartLabels.notAnalysed',
  unreachable_extended: 'widgetLibrary.chartLabels.unreachableExtended',
  total_cost_saved: 'widgetLibrary.chartLabels.totalCostSaved',
  sites_count: 'orgView.kpi.sites',
  good_health_pct: 'orgView.kpi.goodHealth',
  critical_machines: 'orgView.kpi.critical',
  open_events: 'orgView.kpi.openEvents',
  sensors_offline_pct: 'orgView.kpi.sensorsOffline',
  // Asset setup feedback widget (incomplete_asset_setup_breakdown)
  baseline: 'assets.options.analytics.assetSetupGroups.baseline',
  alarms: 'assets.options.analytics.assetSetupGroups.alarms',
  speed_info: 'assets.options.analytics.assetSetupGroups.speed_info',
  others: 'assets.options.analytics.assetSetupGroups.others',
  partially_configured: 'assets.options.analytics.assetSetup.partially_configured',
  not_configured: 'assets.options.analytics.assetSetup.not_configured',
  line_frequency: 'assets.options.analytics.assetSetup.line_frequency',
  gear_ratio: 'assets.options.analytics.assetSetup.gear_ratio',
  speed_ratio: 'assets.options.analytics.assetSetup.speed_ratio',
  // Machine condition buckets (plant_asset_health donut, etc.). Without these, getTranslatedLabel falls back to humanize() → English.
  slight: 'machineCondition.slight',
  moderate: 'machineCondition.moderate',
  warning: 'machineCondition.warning',
  critical: 'machineCondition.critical'
};

const FALLBACK_I18N_KEYS = key => [
  `assets.options.analytics.assetSetupGroups.${key}`,
  `assets.options.analytics.assetSetup.${key}`,
  `assets.options.analytics.events.${key}`,
  `assets.options.alarmMetrics.${key}`,
  `machinesAnalytics.alarmMetrics.${key}`,
  `widgetLibrary.widgets.${key}`,
];

function translationExists(key) {
  return i18n.exists(key);
}

export function getTranslatedLabel(key) {
  const i18nKey = LABEL_TO_I18N_KEY[key];
  if (i18nKey) return i18n.t(i18nKey);

  const fallbackKeys = FALLBACK_I18N_KEYS(key);
  for (let i = 0; i < fallbackKeys.length; i++) {
    if (translationExists(fallbackKeys[i])) {
      return i18n.t(fallbackKeys[i]);
    }
  }

  return humanize(key);
}

const chartTypeFormatterMap = {
  donut: formatDataForDonut,
  trend: formatDataForTrend,
  bar: formatDataForTrend,
  list: formatDataForList,
  count: formatDataForCount,
  metrics: formatDataForMetrics,
  table: formatDataForTable
};

function formatDataForList(res) {
  return [res.items, null];
}

function formatDataForTable(res) {
  return [res, null];
}

function formatDataForMetrics(res, _metadata, filters) {
  return [res.metrics, filters];
}

export function formatDataForCount(res, metadata, filters, state = null) {
  const data = {};
  const isTotalCostSaved = metadata && (metadata.name === 'total_cost_saved' || metadata.id === 'total_cost_saved');

  // Get currency if this is total_cost_saved widget
  let currency = 'USD';
  let currencySymbol = '$';
  if (isTotalCostSaved && state && state.currentAccount && state.currentAccount.preferences) {
    currency = state.currentAccount.preferences.currency || 'USD';
    currencySymbol = getCurrencySymbol(currency);
  }

  ((metadata && metadata.sortOrder) || Object.keys(res)).forEach((key) => {
    if (isTotalCostSaved) {
      // Format with currency for total_cost_saved widget - format all keys
      const value = res[key];
      if (value >= 1000) {
        data[key] = `${currencySymbol} ${round(value / 1000, 1)}k`;
      } else {
        data[key] = `${currencySymbol} ${value}`;
      }
      return;
    }

    // Regular formatting for other widgets
    if (res[key] >= 1000) {
      data[key] = `${round(res[key] / 1000, 1)}k`;
    } else {
      data[key] = `${res[key]}`;
    }
  });

  if (filters === undefined) {
    filters = metadata.filters;
  }
  return [data, filters];
}

function formatDataForDonut(res, metadata) {
  let dataList = [];
  if (metadata.sortOrder) {
    dataList = metadata.sortOrder.map(item => ({ value: res[item], label: getTranslatedLabel(item) }));
  } else {
    Object.keys(res).forEach((key) => {
      if (key !== 'other') dataList.push({ label: getTranslatedLabel(key), value: res[key] });
    });
    if (res.other) dataList.push({ label: getTranslatedLabel('other'), value: res.other });
  }
  return [dataList, metadata.legendFilters || metadata.filters];
}

function formatDataForTrend(res) {
  const data = [[
    i18n.t('common.date', { defaultValue: 'Date' }),
    i18n.t('common.count', { defaultValue: 'Count' }),
  ]];
  const yData = res.counts;
  const xData = convertTimestringsToDateObjects(res.timestamps);
  if (!yData || !xData) return data;
  for (let i = 0; i < yData.length; i++) {
    data[i + 1] = [formatDateWithLocalizedMonthAbbr(xData[i]), yData[i]];
  }
  return [data, null];
}

export const widgetActions = {
  getWidgetsMetadata,
  getWidgetData
};

function getWidgetsMetadata() {
  function request() {
    return { type: widgetConstants.GET_WIDGET_METADATA_REQUEST };
  }
  function success(object) {
    return { type: widgetConstants.GET_WIDGET_METADATA_SUCCESS, object };
  }
  function failure(error) {
    return { type: widgetConstants.GET_WIDGET_METADATA_FAILURE, error };
  }

  return (dispatch, getState) => {
    dispatch(request());

    return handleResponse(axiosInstance.get(ENDPOINT.WIDGETS_METADATA)).then(
      (res) => {
        const metadata = {};
        Object.keys(res).forEach((key) => {
          let feMetadata = keyMetadataMap[key];
          if (keyMetadataMap[key] && keyMetadataMap[key].hasSublevels) {
            const sublevelPath = keyMetadataMap[key].sublevelPath.split('.');
            const sublevelValue = sublevelPath.reduce((acc, curr) => acc[curr], getState());
            feMetadata = keyMetadataMap[key][sublevelValue];
          }
          if (feMetadata && feMetadata.colors && feMetadata.colors.length === 1 && getState().companyReducer.partner) {
            feMetadata.colors = [getState().companyReducer.partner.theme.primaryColor];
          }

          const displayName = res[key].widget_metadata && res[key].widget_metadata.display_name;

          // Widget titles are translated by widget key (stable). Backend display_name is a fallback (English).
          const title = i18n.t(`widgetLibrary.widgets.${key}`, { defaultValue: displayName });

          metadata[key] = {
            ...res[key],
            ...(feMetadata || {}),
            title,
            account_id: getState().user.user.account_id
          };
          const user = getState().user.user;
          if (user && !(isPetasenseAdmin(user) || isPetasenseViewer(user) || isPartnerAdmin(user))) {
            Object.keys(res).forEach((key) => {
              if (res[key].widget_metadata.petasense_admin_only) {
                delete metadata[key];
              }
            });
          }
        });
        dispatch(success(metadata));
      },
      (error) => {
        dispatch(failure(error.message));
      }
    );
  };
}

function getWidgetData(params, metadata, chartType) {
  function request() {
    return { type: widgetConstants.GET_WIDGET_DATA_REQUEST };
  }
  function success() {
    return { type: widgetConstants.GET_WIDGET_DATA_SUCCESS };
  }
  function failure() {
    return { type: widgetConstants.GET_WIDGET_DATA_FAILURE };
  }

  return (dispatch, getState) => {
    dispatch(request());
    return handleResponse(axiosInstance.get(ENDPOINT.WIDGET_DATA, { params })).then(
      (res) => {
        dispatch(success());
        let data; let filterOptions;
        // Get state for currency formatting
        const state = getState();

        if (metadata.dataFormatter) {
          // Pass state for currency formatting if needed
          if ((metadata.name === 'total_cost_saved' || metadata.id === 'total_cost_saved') && chartType === 'count') {
            [data, filterOptions] = formatDataForCount(res, metadata, params.filters, state);
          } else if (metadata.name === 'cost_saved_by_machine' && metadata.dataFormatter) {
            [data, filterOptions] = metadata.dataFormatter(res, metadata, state);
          } else {
            const formatted = metadata.dataFormatter(res, metadata, state);
            if (formatted) {
              [data, filterOptions] = formatted;
            } else {
              [data, filterOptions] = chartTypeFormatterMap[chartType](res, metadata, params.filters);
            }
          }
        } else if (chartType === 'count' && (metadata.name === 'total_cost_saved' || metadata.id === 'total_cost_saved')) {
          [data, filterOptions] = formatDataForCount(res, metadata, params.filters, state);
        } else {
          [data, filterOptions] = chartTypeFormatterMap[chartType](res, metadata, params.filters);
        }
        return [data, filterOptions, res];
      },
      (error) => {
        dispatch(failure(error.message));
        throw error;
      }
    );
  };
}
